// ACC workflow rules (ADR-0002), shared by the API (authoritative) and the UI (display).
// Reproduces the v1.31 prototype's business rules (acc114Coverage, acc114Routing,
// trackMissedTransitions, recordCoverageCompletion, decide, activate, acc114ReopenQuote,
// acc114DueAutomation, quoteVisible) without its data-model defects. Pure functions only.
export const PC_STATUSES = ['Quoting', 'More Info', 'Appointment Scheduled', 'Schedule Closing', 'Missed', 'Completed', 'Brokerage'];
export const LIFE_STATUSES = ['Interview', 'Outstanding (WF)', 'Final Requirements', 'Schedule Closing', 'Appointment Scheduled', 'Completed', 'Missed'];
export const QUOTE_TYPES = ['New', 'Requote', 'Win Back', 'Current FB', 'Cold Call', 'Break Off', 'Progressive'];
export const CALL_STATUSES = ['Outgoing', 'Completed', 'Policy Change', 'Follow Up Required'];
export const AGENCY_ROLES = ['AgencyOwner', 'OfficeManager', 'Employee'];
const FINAL = ['Completed', 'Missed'];
export const isPcStatus = (v) => typeof v === 'string' && PC_STATUSES.includes(v);
export const isLifeStatus = (v) => typeof v === 'string' && LIFE_STATUSES.includes(v);
export const isQuoteType = (v) => typeof v === 'string' && QUOTE_TYPES.includes(v);
export const isCallStatus = (v) => typeof v === 'string' && CALL_STATUSES.includes(v);
export const isManager = (role) => role === 'AgencyOwner' || role === 'OfficeManager';
export function coverage(q) {
    const selected = [q.pcStatus, q.lifeStatus].filter((x) => Boolean(x));
    return {
        pc: q.pcStatus,
        life: q.lifeStatus,
        active: selected.some((x) => !FINAL.includes(x)),
        done: selected.includes('Completed'),
        missed: selected.includes('Missed'),
        onlyMissed: selected.length > 0 && selected.every((x) => x === 'Missed'),
    };
}
export function routing(q) {
    const c = coverage(q);
    if (c.active)
        return 'In Progress';
    if (c.done && c.missed)
        return 'Completed + Follow-up';
    if (c.done)
        return 'Completed Sales';
    if (c.missed)
        return 'Requotes / Follow-up';
    return 'In Progress';
}
export const isBrokerageFollowUp = (q) => q.type === 'Progressive' && q.pcStatus === 'Brokerage';
/** Shown in Requotes / Follow-up: nothing active and something missed, or a Progressive brokerage quote. */
export function isFollowUp(q) {
    const c = coverage(q);
    return (!c.active && c.missed) || isBrokerageFollowUp(q);
}
/** The prototype's active() set (dashboard cards and active quoting). */
export function isActiveQuote(q) {
    return q.pcStatus !== 'Completed' && !isBrokerageFollowUp(q) && !(q.pcStatus === 'Missed' && q.approval === 'Approved');
}
/**
 * Shown in Quote Management's "In progress" list (prototype acc114QuoteTable): a coverage is still
 * being worked, or none is chosen yet. So P/C Completed with Life still open stays listed.
 * Brokerage follow-ups are listed under Follow-ups only.
 */
export function isInProgress(q) {
    const c = coverage(q);
    return (c.active || (!q.pcStatus && !q.lifeStatus)) && !isBrokerageFollowUp(q);
}
export const missedCoverages = (q) => [q.pcStatus === 'Missed' ? 'PC' : null, q.lifeStatus === 'Missed' ? 'Life' : null].filter((x) => x !== null);
export const missedKindLabel = (q) => missedCoverages(q).map((c) => (c === 'PC' ? 'P/C' : 'Life')).join(' + ');
/** Approval follows the P/C status: entering Missed requests approval; leaving Missed clears it. */
export function approvalAfterPcChange(before, after, current) {
    if (after === 'Missed')
        return before === 'Missed' ? current : 'Pending';
    return '';
}
/**
 * Events produced by a status change (append-only history). Mirrors the prototype:
 * Missed when a coverage becomes Missed; Reopened when it leaves Missed; Completed when it
 * becomes Completed; Recovered when a coverage that was missed and reopened is Completed.
 * `openMisses` = coverages with a Missed event not yet Reopened; `reopenedUnrecovered` =
 * coverages reopened after a miss but not yet recovered.
 */
export function eventsForChange(before, after, reopenedUnrecovered = []) {
    const out = [];
    const owner = before.ownerMembershipId ?? after.ownerMembershipId;
    const pairs = [['PC', before.pcStatus, after.pcStatus], ['Life', before.lifeStatus, after.lifeStatus]];
    for (const [cov, from, to] of pairs) {
        if (from === to)
            continue;
        if (from === 'Missed')
            out.push({ coverage: cov, type: 'Reopened', ownerMembershipId: owner });
        if (to === 'Missed')
            out.push({ coverage: cov, type: 'Missed', ownerMembershipId: owner });
        if (to === 'Completed') {
            out.push({ coverage: cov, type: 'Completed', ownerMembershipId: after.ownerMembershipId ?? owner });
            const wasReopened = reopenedUnrecovered.includes(cov) || from === 'Missed';
            if (wasReopened)
                out.push({ coverage: cov, type: 'Recovered', ownerMembershipId: after.ownerMembershipId ?? owner });
        }
    }
    return out;
}
/**
 * Reopening a missed coverage (approved request or due follow-up): owner retained, completed
 * coverage untouched. Origin becomes Requote, which reports count as recovered business.
 */
export function reopenMissed(q, today) {
    return {
        ...q,
        pcStatus: q.pcStatus === 'Missed' ? 'Quoting' : q.pcStatus,
        lifeStatus: q.lifeStatus === 'Missed' ? 'Interview' : q.lifeStatus,
        approval: '',
        type: 'Requote',
        origin: 'Requote',
        dueDate: addDays(today, 3),
    };
}
/** Management "Activate": approved missed P/C (or a brokerage follow-up) goes back to Quoting. */
export function canActivate(q) {
    return (q.pcStatus === 'Missed' && q.approval === 'Approved') || isBrokerageFollowUp(q);
}
export function activate(q, today) {
    const type = isBrokerageFollowUp(q) ? 'Progressive' : 'Requote';
    return { ...q, pcStatus: 'Quoting', type, origin: type, approval: '', dueDate: addDays(today, 3) };
}
/** Follow-ups whose due date has arrived are reopened by management automation (brokerage excluded). */
export function isDueForReopen(q, today) {
    return isFollowUp(q) && Boolean(q.dueDate) && q.dueDate <= today && !isBrokerageFollowUp(q);
}
/** Who may see an opportunity (prototype quoteVisible). */
export function canSeeQuote(viewer, q) {
    return isManager(viewer.role) || viewer.seesAllQuotes || q.ownerMembershipId === viewer.membershipId;
}
/** An employee limited to their own quotes (baseline 9.16-9.17). */
export const isRestricted = (viewer) => !isManager(viewer.role) && !viewer.seesAllQuotes;
/**
 * Who may edit (prototype saveQuote and the 1.14 openQuote lock): a card whose every coverage is
 * Missed is read-only for everyone until it is reopened, activated or decided on the Approvals
 * page; otherwise managers edit any, others visible quotes except requote records.
 */
export function canEditQuote(viewer, q) {
    if (coverage(q).onlyMissed)
        return false;
    if (isManager(viewer.role))
        return true;
    if (!canSeeQuote(viewer, q))
        return false;
    return !((q.pcStatus === 'Missed' && q.approval === 'Approved') || isBrokerageFollowUp(q));
}
// ---------- dates (agency-local calendar days) ----------
export const AGENCY_TIME_ZONE = 'America/Chicago';
export function todayIn(timeZone = AGENCY_TIME_ZONE, now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
export function addDays(isoDate, days) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
export const isIsoDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
export const ROLE_LABELS = { AgencyOwner: 'Agency Owner', OfficeManager: 'Office Manager', Employee: 'Employee' };
export * from './reports.js';
export * from './salesCycle.js';
//# sourceMappingURL=index.js.map